import { Link } from 'react-router-dom';
import { ShoppingBag, Trash2, Minus, Plus, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { formatPrice } from '@/lib/api';

export default function CartPage() {
    const { cart, cartCount, updateCart, removeFromCart, clearCart } = useCart();

    if (cart.items.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-4" data-testid="cart-empty">
                <ShoppingBag className="w-20 h-20 text-gray-200 mb-6" />
                <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Your Cart is Empty</h1>
                <p className="font-body text-gray-500 mb-6 text-center">Looks like you haven't added anything to your cart yet.</p>
                <Link to="/shop"><Button className="bg-pink-600 hover:bg-pink-700 rounded-full px-8 font-body" data-testid="start-shopping-btn">Start Shopping</Button></Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50" data-testid="cart-page">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Persistent Continue Shopping link at top */}
                <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm font-body text-pink-600 hover:text-pink-700 mb-3 tap-target" data-testid="continue-shopping-top">
                    <ArrowLeft className="w-4 h-4" /> Continue Shopping
                </Link>
                <div className="flex items-center justify-between mb-6">
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Shopping Cart ({cartCount})</h1>
                    <button onClick={clearCart} className="text-sm font-body text-gray-500 hover:text-red-500 transition-colors tap-target" data-testid="clear-cart-btn">Clear All</button>
                </div>
                <div className="space-y-3">
                    {cart.items.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-4 flex gap-4 shadow-sm" data-testid={`cart-page-item-${idx}`}>
                            <Link to={`/product/${item.slug}`} className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover"  loading="lazy" decoding="async" />
                            </Link>
                            <div className="flex-1 min-w-0">
                                <Link to={`/product/${item.slug}`}><h3 className="font-body font-medium text-gray-900 text-sm sm:text-base hover:text-pink-600 transition-colors">{item.name}</h3></Link>
                                {item.variant && <p className="text-xs font-body text-gray-500 mt-0.5">{item.variant}</p>}
                                <p className="text-base sm:text-lg font-bold text-pink-600 font-body mt-1">{formatPrice(item.price)}</p>
                                <div className="flex items-center justify-between mt-2">
                                    <div className="flex items-center border border-gray-200 rounded-full">
                                        <button onClick={() => updateCart(item.product_id, item.quantity - 1, item.variant)} className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-50 rounded-l-full active:bg-gray-100 tap-target" data-testid={`cart-page-decrease-${idx}`} aria-label="Decrease quantity"><Minus className="w-3.5 h-3.5" /></button>
                                        <span className="w-10 sm:w-8 text-center text-sm font-body font-medium select-none">{item.quantity}</span>
                                        <button onClick={() => updateCart(item.product_id, item.quantity + 1, item.variant)} className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-50 rounded-r-full active:bg-gray-100 tap-target" data-testid={`cart-page-increase-${idx}`} aria-label="Increase quantity"><Plus className="w-3.5 h-3.5" /></button>
                                    </div>
                                    <button onClick={() => removeFromCart(item.product_id, item.variant)} className="text-gray-400 hover:text-red-500 p-2.5 tap-target" data-testid={`cart-page-remove-${idx}`} aria-label="Remove item">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {/* Summary */}
                <div className="bg-white rounded-xl p-6 mt-6 shadow-sm">
                    <div className="flex justify-between text-lg font-body">
                        <span className="font-medium text-gray-700">Subtotal</span>
                        <span className="font-bold text-pink-600">{formatPrice(cart.total)}</span>
                    </div>
                    <p className="text-sm font-body text-gray-500 mt-1">Shipping calculated at checkout</p>
                    <Link to="/checkout" className="block mt-4">
                        <Button className="w-full bg-pink-600 hover:bg-pink-700 rounded-full h-12 text-base font-body gap-2" data-testid="proceed-checkout-btn">
                            Proceed to Checkout <ArrowRight className="w-5 h-5" />
                        </Button>
                    </Link>
                    <Link to="/shop" className="block mt-3">
                        <Button variant="outline" className="w-full rounded-full border-pink-200 text-pink-700 hover:bg-pink-50 font-body" data-testid="continue-shopping-btn">Continue Shopping</Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
